/**
 * The device benchmark's report contract — the single shape both phases produce into and the
 * one document `/benchmark` POSTs to `/benchmark-report`.
 *
 * This file is deliberately **types and pure assembly only**: no OPFS, no worker, no Pixi, no
 * `fetch`. The two phases (`benchmark-sim-phase.ts`, `benchmark-render-phase.ts`) are written
 * against it independently, and the screen assembles their results without either phase
 * learning about the other.
 *
 * ## What the shape is defending against
 *
 * Three honesty properties from the `client-benchmark` spec are enforced by the TYPES here
 * rather than by discipline at the call sites, because a later edit is the realistic threat:
 *
 * 1. **A run that died is never reported as completed.** `SimPhaseSettled` carries a
 *    `terminalCondition` and a `completed` flag derived from it, and `ticksStepped` is the
 *    count actually stepped — never the requested 3000. At the shipped solo geometry a colony
 *    is measured NOT to survive the horizon, so the short run is the expected case.
 * 2. **No single extrapolated full-match figure is published.** There is no field for one, and
 *    {@link assembleReport} has no arithmetic that could invent one. Throughput is reported
 *    per segment alongside peak population so a reader can see the growth curve.
 * 3. **Frame figures are pacing, not GPU cost.** The render figures live under a
 *    `framePacing` key whose docs say what they are, and {@link REPORT_FORBIDDEN_FIELD_PATTERN}
 *    exists so a test can assert no field is named or presented as GPU cost.
 *
 * A phase that failed, or whose measurement the environment disturbed, is RECORDED as such
 * rather than omitted — an absent phase and a failed phase are different facts, and only one
 * of them is true.
 */

/** The schema version of the report document. Bump when a field's meaning changes. */
export const BENCHMARK_REPORT_VERSION = 1;

/** Where the client POSTs its report. A distinct root segment from the `/benchmark` route. */
export const BENCHMARK_REPORT_PATH = '/benchmark-report';

/**
 * The fixed seed every device runs, so two devices are compared on the same trajectory rather
 * than on two different early deaths.
 *
 * **It is a literal on purpose.** Deriving it from a clock, an RNG or any device property
 * would silently destroy the comparability this whole instrument exists to provide, and the
 * failure would be invisible in the report. A test asserts this constant is what the run uses.
 */
export const BENCHMARK_SEED = 'waggle-device-benchmark-1';

/** The preset name the run declares, and the one the server serves TOML for. */
export const BENCHMARK_BALANCE_PRESET = 'benchmark';

/** How the simulation phase ended. */
export type SimTerminalCondition =
  /** The colony was lost before the horizon. The expected case at the solo geometry. */
  | 'colony_lost'
  /** The run reached its 600 s horizon with the colony alive. */
  | 'clock_expired';

/** One bucket of the run, so per-tick cost's growth with population stays visible. */
export interface ThroughputSegment {
  /** First tick of the segment, inclusive. */
  readonly fromTick: number;
  /** Last tick of the segment, inclusive. */
  readonly toTick: number;
  /** Ticks stepped per wall-clock second across this segment alone. */
  readonly ticksPerSecond: number;
  /** The colony's bee count at the end of the segment. */
  readonly bees: number;
}

/** The simulation phase's result when it settled. */
export interface SimPhaseSettled {
  readonly status: 'settled';
  /** How the run ended — never assumed. */
  readonly terminalCondition: SimTerminalCondition;
  /**
   * True only for `clock_expired`. A run ending in colony loss is not a completed one, and
   * {@link simSettled} is the only constructor, so the two cannot drift apart.
   */
  readonly completed: boolean;
  /** Ticks ACTUALLY stepped. Seed-dependent and usually short of the requested horizon. */
  readonly ticksStepped: number;
  /** The horizon that was requested, recorded so the shortfall is legible. */
  readonly requestedTicks: number;
  /** Highest bee count seen across the run. */
  readonly peakBees: number;
  /** Throughput per bucket. Never collapsed into one figure. */
  readonly segments: readonly ThroughputSegment[];
  /** Time inside `step`, excluding setup, module load and teardown. */
  readonly steppingMs: number;
  /** Wall time for the whole phase, including module load, OPFS open and trace publication. */
  readonly totalMs: number;
  /** The fixed seed the run used. */
  readonly seed: string;
  /** The preset name the run's manifest declares. */
  readonly balancePreset: string;
}

/** The simulation phase's result when it did not settle. */
export interface PhaseFailed {
  readonly status: 'failed';
  /** A short machine-readable cause, e.g. `preset_unavailable`, `worker_crashed`. */
  readonly reason: string;
}

export type SimPhaseResult = SimPhaseSettled | PhaseFailed;

/** Frame-time distribution. Percentiles are in milliseconds; rates are frames per second. */
export interface FramePacing {
  /** How many measured frames the statistics are computed from. Warm-up is NOT included. */
  readonly sampleCount: number;
  /** Frames spent warming up and excluded — recorded, never silently discarded. */
  readonly warmupFrameCount: number;
  /** Wall time the warm-up consumed. */
  readonly warmupMs: number;
  readonly minFps: number;
  readonly maxFps: number;
  readonly meanFps: number;
  /** Frame interval in ms at the 50th, 95th and 99th percentile. */
  readonly p50FrameMs: number;
  readonly p95FrameMs: number;
  readonly p99FrameMs: number;
}

/** The render phase's result when it produced usable figures. */
export interface RenderPhaseSettled {
  readonly status: 'settled';
  /**
   * CPU-side submission plus rAF/vsync pacing — what a player feels. **Not GPU cost**:
   * nothing in the render path forces a GPU sync, so backpressure is absorbed by buffering.
   */
  readonly framePacing: FramePacing;
  /** Bees in the synthetic scene. */
  readonly beeCount: number;
  /** The camera scale the scene was measured at — asserted above the symbol-mode threshold. */
  readonly cameraScale: number;
  /** The animation mode held for the measured window. */
  readonly animationMode: string;
}

/**
 * The render phase's result when the environment disturbed the measured window.
 *
 * On a phone this is the common case, not an edge case. It carries NO frame figures at all:
 * publishing a distribution composed mostly of throttled frames would be a confident wrong
 * number, which is worse than no number.
 */
export interface RenderPhaseInvalidated {
  readonly status: 'invalidated';
  /** Why the window was invalidated, e.g. `visibility_lost`. */
  readonly reason: string;
}

export type RenderPhaseResult = RenderPhaseSettled | RenderPhaseInvalidated | PhaseFailed;

/** What the device reports about itself. Best-effort: an absent facet is `null`, never faked. */
export interface DeviceDetails {
  readonly userAgent: string | null;
  readonly platform: string | null;
  readonly hardwareConcurrency: number | null;
  /** `navigator.deviceMemory`, in GiB. Chromium-only; `null` elsewhere. */
  readonly deviceMemoryGb: number | null;
  readonly screenWidth: number | null;
  readonly screenHeight: number | null;
  readonly devicePixelRatio: number | null;
  readonly language: string | null;
}

/** The one document submitted per benchmark run. */
export interface BenchmarkReport {
  readonly version: number;
  readonly device: DeviceDetails;
  readonly simulation: SimPhaseResult;
  readonly render: RenderPhaseResult;
}

/**
 * Constructs a settled simulation result, deriving `completed` from the terminal condition so
 * a caller cannot claim a lost colony completed its run.
 */
export function simSettled(
  fields: Omit<SimPhaseSettled, 'status' | 'completed'>,
): SimPhaseSettled {
  return {
    ...fields,
    status: 'settled',
    completed: fields.terminalCondition === 'clock_expired',
  };
}

/** Constructs a failed phase result. */
export function phaseFailed(reason: string): PhaseFailed {
  return { status: 'failed', reason };
}

/**
 * Assembles the submitted document. Pure: it computes nothing, derives nothing and — in
 * particular — extrapolates nothing. Every figure in the output was measured by a phase.
 */
export function assembleReport(
  device: DeviceDetails,
  simulation: SimPhaseResult,
  render: RenderPhaseResult,
): BenchmarkReport {
  return { version: BENCHMARK_REPORT_VERSION, device, simulation, render };
}

/**
 * Field names a report must never carry, as a test-facing regex.
 *
 * The render figures are frame pacing. A later edit that renamed them `gpuMs`, or added a
 * `gpuCost` alongside, would turn an honest measurement into a false claim without changing
 * a single number — so the prohibition is asserted structurally over the serialised document
 * rather than trusted to the docs above.
 */
export const REPORT_FORBIDDEN_FIELD_PATTERN = /gpu/i;

/**
 * Field names that would present a single extrapolated full-match figure — the artefact
 * `rounds-practice-sandbox` task 5.14 disqualified. Asserted the same way and for the same
 * reason: the danger is a later well-meaning addition, not today's code.
 */
export const REPORT_FORBIDDEN_EXTRAPOLATION_PATTERN =
  /extrapolat|projectedmatch|fullmatch|estimatedmatch/i;
